import { Box, IconButton, IconButtonProps, useTheme } from '@mui/material';
import React from 'react';
import { themeColors } from '../styles/colors';

export interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

/**
 * ExpandMore renders the rotating icon button used for collapsible sections.
 * @param props - IconButton props plus expanded state for rotation.
 * @returns A rotating expand/collapse button.
 */
export const ExpandMore = ({
  expand,
  children,
  ...iconButtonProps
}: ExpandMoreProps) => {
  const theme = useTheme();
  const buttonRef = React.useRef<HTMLElement | null>(null);
  const previousExpandRef = React.useRef(expand);

  React.useEffect(() => {
    const buttonElement = buttonRef.current;
    if (!buttonElement) {
      return undefined;
    }

    const previousRotation = previousExpandRef.current ? 180 : 0;
    const nextRotation = expand ? 180 : 0;
    previousExpandRef.current = expand;

    const animation = buttonElement.animate(
      [
        { transform: `rotate(${previousRotation}deg)` },
        { transform: `rotate(${nextRotation}deg)` },
      ],
      {
        duration: theme.transitions.duration.shortest,
        easing: theme.transitions.easing.easeInOut,
        fill: 'forwards',
      }
    );

    return () => {
      animation.cancel();
    };
  }, [expand, theme.transitions.duration.shortest, theme.transitions.easing.easeInOut]);

  return (
    <Box
      ref={buttonRef}
      component={IconButton}
      color={themeColors.onSurfaceVariant}
      {...iconButtonProps}
    >
      {children}
    </Box>
  );
};
